import {
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ContractorType,
  OperatingModeEnum,
  OrderDetail,
  PossessionBaseEnum,
  PriceRCA,
  Status9e7Enum,
  VehicleType
} from '@insurance/api/requests'
import { ProductCode } from '@insurance/types'
import { getUnderwritingWarnings } from '@insurance/utils'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Details } from './Details'
import { InformationStep } from './InformationStep'
import { MainStep } from './MainStep'
import { RcaInsuranceProvider } from './RcaInsuranceContext'
import { RcaInsuranceControls } from './RcaInsuranceControls'
import {
  formProps,
  getRcaValuesFromStorage,
  normalizeOrderData,
  normalizeRcaData,
  removeRcaValuesFromStorage,
  saveRcaValuesToStorage
} from './utils'

const forms = [<MainStep key={0} />, <InformationStep key={1} />]

const {
  stepKey,
  priceKey,
  orderKey,
  vehicleDataProps,
  vehicleTypeProps,
  contractorTypeProps,
  certificateNumberProps,
  trailerCertificateNumberProps,
  operatingModeProps,
  validFromProps,
  vehicleSelectedProps,
  identificationNumberProps,
  possessionBaseProps,
  possessionBaseDocNumberProps,
  possessionBaseDocDateProps,
  possessionBaseDocOtherTitleProps,
  possessionBaseDocNoteProps,
  isPropertyKey,
  warningsKey
} = formProps()

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [orderKey.name]: undefined,
  [vehicleDataProps.name]: undefined,
  [identificationNumberProps.name]: undefined,
  [vehicleTypeProps.name]: VehicleType.VEHICLE,
  [contractorTypeProps.name]: ContractorType.PERSON,
  [certificateNumberProps.name]: undefined,
  [trailerCertificateNumberProps.name]: undefined,
  [operatingModeProps.name]: OperatingModeEnum._7,
  [validFromProps.name]: undefined,
  [vehicleSelectedProps.name]: undefined,
  [possessionBaseProps.name]: undefined,
  [possessionBaseDocNumberProps.name]: undefined,
  [possessionBaseDocDateProps.name]: undefined,
  [possessionBaseDocOtherTitleProps.name]: undefined,
  [possessionBaseDocNoteProps.name]: undefined,
  [isPropertyKey.name]: undefined,
  [warningsKey.name]: undefined
}

export const RcaInsurance = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)

  const { mutate: calculatePrice, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate()

  const { mutate: recalculateOrder, isPending: isRecalculatingLoading } =
    useSalesServiceSalesOrdersPartialUpdate()

  const handleCalculate = async (onSuccess: () => void) => {
    await form.validateFields()
    const values = form.getFieldsValue(true)
    calculatePrice(
      {
        code: ProductCode.RCA,
        requestBody: normalizeRcaData(values)
      },
      {
        onSuccess: (priceResponse) => {
          const priceData = priceResponse as PriceRCA
          const rcaData = priceData.data
          form.setFieldValue(priceKey.name, {
            amount: Number(Number(priceData.amount)?.toFixed(2))
          })
          form.setFieldValue(vehicleDataProps.name, rcaData)
          form.setFieldValue(orderKey.name, priceData.order?.id)
          form.setFieldValue(
            validFromProps.name,
            rcaData?.minimal_start ? dayjs(rcaData?.minimal_start) : dayjs()
          )
          form.setFieldValue(isPropertyKey.name, priceData?.is_property)
          form.setFieldValue(
            possessionBaseProps.name,
            priceData.is_property ? PossessionBaseEnum.PROPERTY : undefined
          )

          const warnings = getUnderwritingWarnings(priceData)
          form.setFieldValue(warningsKey.name, warnings)

          onSuccess()
        },
        onError: (error) => showApiErrors(error, form)
      }
    )
  }

  const handleRecalculate = () => {
    const values = form.getFieldsValue(true)
    const orderId = values[orderKey.name] as number | undefined
    if (!orderId) return

    recalculateOrder(
      replaceEmptyWithUndefined({
        id: orderId,
        requestBody: {
          status: Status9e7Enum.DATA_ENTRY,
          stage: 0,
          rca: normalizeOrderData(values)
        }
      }),
      {
        onSuccess: (order: OrderDetail) => {
          form.setFieldValue(priceKey.name, {
            amount: Number(Number(order.amount)?.toFixed(2))
          })
          // Recalculate only changes operating mode / possession base — the driver's
          // licence status is unchanged, so keep the warnings from the price call.
          form.setFields(
            form.getFieldsError().map(({ name }) => ({ name, errors: [] }))
          )
        },
        onError: (error) => showApiErrors(error, form, { unwrapKeys: ['rca'] })
      }
    )
  }

  const handleReadyToSaveData = () => setIsReady(true)

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)
    saveRcaValuesToStorage(values)
    return null
  }

  useEffect(() => {
    const stored = getRcaValuesFromStorage()
    form.setFieldsValue({ ...initValues, ...stored })
  }, [form])

  useEffect(() => {
    const stored = getRcaValuesFromStorage()
    if (!stored) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeRcaValuesFromStorage()
        setIsReady(false)
        form.setFieldsValue(initValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t])

  return (
    <RcaInsuranceProvider
      isEditable={false}
      isCalculatingLoading={isCalculatingLoading || isRecalculatingLoading}
      handleCalculate={handleCalculate}
      handleRecalculate={handleRecalculate}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue('step')]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <RcaInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </RcaInsuranceProvider>
  )
}
